import content from "@/content/site-content.json";
import type { SiteContent } from "@/types/content";

const { hero } = content as SiteContent;

/**
 * Signaturelementet på siden: en ring av sammenkoblede figurer,
 * tegnet direkte etter logoens formspråk (mennesker som holder
 * rundt hverandre i en sirkel). Den roterer sakte i bakgrunnen av
 * heroen — respekterer prefers-reduced-motion via global.css.
 */
function RingOfHands() {
  const figures = 8;
  return (
    <svg
      className="ring-of-hands"
      viewBox="0 0 400 400"
      aria-hidden="true"
      focusable="false"
    >
      <g>
        {Array.from({ length: figures }).map((_, i) => {
          const angle = (i / figures) * 2 * Math.PI - Math.PI / 2;
          const r = 150;
          const cx = 200 + r * Math.cos(angle);
          const cy = 200 + r * Math.sin(angle);
          const colors = [
            "var(--color-apricot)",
            "var(--color-blush)",
            "var(--color-terracotta)",
            "var(--color-clay)",
            "var(--color-rosewood)",
          ];
          const fill = colors[i % colors.length];
          return (
            <g key={i} transform={`translate(${cx} ${cy})`}>
              <circle r="14" cy="-20" fill={fill} />
              <path
                d="M -16 26 C -16 0 16 0 16 26 L 16 30 L -16 30 Z"
                fill={fill}
              />
            </g>
          );
        })}
      </g>
    </svg>
  );
}

export default function Hero() {
  return (
    <section className="hero">
      <div className="container hero__inner">
        <div className="hero__text">
          <p className="eyebrow">{hero.eyebrow}</p>
          <h1>{hero.title}</h1>
          <p className="hero__subtitle">{hero.subtitle}</p>
          <div className="hero__ctas">
            <a href={hero.primaryCta.href} className="btn btn-primary">
              {hero.primaryCta.label}
            </a>
            <a href={hero.secondaryCta.href} className="btn btn-ghost">
              {hero.secondaryCta.label}
            </a>
          </div>
        </div>
        <div className="hero__art" role="presentation">
          <RingOfHands />
        </div>
      </div>

      <style>{`
        .hero {
          padding: var(--space-5) 0 var(--space-4);
          overflow: hidden;
        }
        .hero__inner {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: var(--space-4);
          align-items: center;
        }
        .hero__subtitle {
          font-size: 1.1rem;
          color: var(--color-ink-soft);
        }
        .hero__ctas {
          display: flex;
          gap: var(--space-2);
          flex-wrap: wrap;
          margin-top: var(--space-3);
        }
        .hero__art {
          display: flex;
          justify-content: center;
        }
        .ring-of-hands {
          width: min(360px, 90%);
          animation: spin 60s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 860px) {
          .hero__inner {
            grid-template-columns: 1fr;
          }
          .hero__art {
            order: -1;
          }
          .ring-of-hands {
            width: min(240px, 70%);
          }
        }
      `}</style>
    </section>
  );
}
